import * as XLSX from 'xlsx'

export type CompreAgoraFileRole = 'estoque' | 'entradas' | 'otif'

export interface CompreAgoraSourceFile {
  name: string
  data: ArrayBuffer | Uint8Array
}

export interface CompreAgoraParserOptions {
  leadTimeDays: number
  targetCoverageDays: number
  daysConsideredByMonth?: Record<string, number>
  storeCnpjByKey?: Record<string, string>
}

export type CompreAgoraClassification =
  | 'Ruptura instalada'
  | 'Sem venda(avaliar)'
  | 'Ruptura iminente'
  | 'Perto do mínimo'
  | 'Cobertura crítica'
  | 'Excesso'
  | 'Saudável'

export interface CompreAgoraAnalyticRow {
  anoMes: string
  cnpjLoja: string
  gtin: string
  produto: string
  vendaQtdMes: number
  vendaRs: number
  comprasQtd: number
  comprasRs: number
  estoqueAtualQtd: number
  precoMedioRs: number
  diasDoMes: number
  cmd: number
  coberturaAtualDias: number
  ltDias: number
  flagLtDefault: number
  coberturaAlvoDias: number
  flagCoberturaDefault: number
  ss: number
  ddlt: number
  rop: number
  estoqueAlvo: number
  pedidoSugerido: number
  flagSemVenda: number
  qtdAjustadaUsuario: number | null
  pedidoFinalQtd: number
  origemPedido: 'Modelo' | 'Usuário'
  flagAjustado: number
  classificacao: CompreAgoraClassification
  chave: string
}

export interface CompreAgoraImportRow {
  ean: string
  produto: string
  cnpjLoja: string
  saldoInicial: number
  entrada: number
  saida: number
  saldoFinal: number
  estoqueAtual: number
}

export interface CompreAgoraRoleSummary {
  role: CompreAgoraFileRole
  fileNames: string[]
  rowCount: number
}

export interface CompreAgoraBundleResult {
  matched: boolean
  errors: string[]
  analyticRows: CompreAgoraAnalyticRow[]
  importRows: CompreAgoraImportRow[]
  roleSummaries: CompreAgoraRoleSummary[]
  unknownFiles: string[]
}

type SheetRow = unknown[]

interface DetectedSource {
  fileName: string
  role: CompreAgoraFileRole
  rows: SheetRow[]
  headerRowIndex: number
}

interface StockAggregate {
  anoMes: string
  cnpjLoja: string
  gtin: string
  produto: string
  vendaQtdMes: number
  estoqueAtualQtd: number
  saldoInicial: number
  saldoFinal: number
}

interface EntryAggregate {
  comprasQtd: number
  comprasRs: number
}

const REQUIRED_HEADERS: Record<CompreAgoraFileRole, string[]> = {
  estoque: [
    'cnpj_fornecedor',
    'fornecedor',
    'fornecedor_divisao',
    'data_referencia',
    'cnpj_loja',
    'ean',
    'descricao_produto',
    'saldo_inicial',
    'entrada',
    'saida',
    'saldo_final',
    'estoque_atual',
  ],
  entradas: [
    'data_recebimento',
    'loja_key',
    'cnpj_fornecedor',
    'fornecedor',
    'gtin_principal',
    'desc_produto',
    'quantidade_recebida',
    'ni_valor_bruto',
    'chave',
  ],
  otif: [
    'id_pedido',
    'data_pedido',
    'data_prevista_entrega',
    'cnpj_loja',
    'ean',
    'quantidade_entregue',
    'cnpj_fornecedor',
    'fornecedor',
  ],
}

const ROLE_LABELS: Record<CompreAgoraFileRole, string> = {
  estoque: 'Estoque (Anexo 1)',
  entradas: 'Entradas (Anexo 2)',
  otif: 'OTIF (Anexo 3)',
}

export const DEFAULT_STORE_CNPJ_BY_KEY: Record<string, string> = {
  '1': '993000100',
  '2': '993000444',
  '3': '993000363',
  '5': '993000606',
  '7': '993000797',
  '8': '993000878',
  '9': '993000959',
  '12': '993001092',
}

const KEY_SEPARATOR = '\u001f'

function getCellText(value: unknown): string {
  if (value instanceof Date) return value.toISOString()
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  return String(value ?? '')
    .replace(/\s+/g, ' ')
    .trim()
}

export function normalizeCompreAgoraHeader(value: unknown): string {
  return getCellText(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_')
    .trim()
}

function findHeaderRole(row: SheetRow): CompreAgoraFileRole | null {
  const headers = new Set(row.map(normalizeCompreAgoraHeader).filter(Boolean))
  const matches = (Object.keys(REQUIRED_HEADERS) as CompreAgoraFileRole[]).filter((role) =>
    REQUIRED_HEADERS[role].every((header) => headers.has(header))
  )

  return matches.length === 1 ? matches[0] : null
}

function readDetectedSources(files: CompreAgoraSourceFile[]): {
  detected: DetectedSource[]
  unknownFiles: string[]
  readErrors: string[]
} {
  const detected: DetectedSource[] = []
  const unknownFiles: string[] = []
  const readErrors: string[] = []

  for (const file of files) {
    try {
      const workbook = XLSX.read(file.data, {type: 'array', cellDates: true})
      const matches: DetectedSource[] = []

      for (const sheetName of workbook.SheetNames) {
        const worksheet = workbook.Sheets[sheetName]
        const rows = XLSX.utils.sheet_to_json<SheetRow>(worksheet, {
          header: 1,
          defval: '',
          raw: true,
        })

        for (let rowIndex = 0; rowIndex < Math.min(rows.length, 10); rowIndex++) {
          const role = findHeaderRole(rows[rowIndex])
          if (!role) continue
          matches.push({fileName: file.name, role, rows, headerRowIndex: rowIndex})
          break
        }
      }

      const distinctRoles = Array.from(new Set(matches.map((match) => match.role)))
      if (distinctRoles.length === 0) {
        unknownFiles.push(file.name)
      } else if (distinctRoles.length > 1) {
        readErrors.push(
          `${file.name}: o arquivo contém mais de um layout CompreAgora. Envie Estoque, Entradas e OTIF em arquivos separados.`
        )
      } else {
        detected.push(matches.find((match) => match.role === distinctRoles[0]) as DetectedSource)
      }
    } catch {
      unknownFiles.push(file.name)
    }
  }

  return {detected, unknownFiles, readErrors}
}

export function detectCompreAgoraBundle(files: CompreAgoraSourceFile[]): {
  matched: boolean
  roles: CompreAgoraFileRole[]
  unknownFiles: string[]
} {
  const {detected, unknownFiles, readErrors} = readDetectedSources(files)
  return {
    matched: detected.length > 0 || readErrors.length > 0,
    roles: Array.from(new Set(detected.map((source) => source.role))),
    unknownFiles,
  }
}

function buildColumnMap(header: SheetRow): Record<string, number> {
  return header.reduce<Record<string, number>>((map, value, index) => {
    const normalized = normalizeCompreAgoraHeader(value)
    if (normalized) map[normalized] = index
    return map
  }, {})
}

function parseNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  let text = getCellText(value).replace(/[^\d,.-]/g, '')
  if (!text || !/\d/.test(text)) return null

  const lastComma = text.lastIndexOf(',')
  const lastDot = text.lastIndexOf('.')
  if (lastComma !== -1 && lastDot !== -1) {
    text = lastComma > lastDot ? text.replace(/\./g, '').replace(',', '.') : text.replace(/,/g, '')
  } else if (lastComma !== -1) {
    text = text.replace(',', '.')
  }

  const parsed = Number(text)
  return Number.isFinite(parsed) ? parsed : null
}

function parseIdentifier(value: unknown): string {
  const text = getCellText(value)
  return text.endsWith('.0') ? text.slice(0, -2) : text
}

function toYearMonth(year: number, month: number): string | null {
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    year < 1900 ||
    month < 1 ||
    month > 12
  ) {
    return null
  }
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}`
}

function parseYearMonth(value: unknown): string | null {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return toYearMonth(value.getUTCFullYear(), value.getUTCMonth() + 1)
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    const parsed = XLSX.SSF.parse_date_code(value)
    return parsed ? toYearMonth(parsed.y, parsed.m) : null
  }

  const text = getCellText(value)
  const brDate = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (brDate) return toYearMonth(Number(brDate[3]), Number(brDate[2]))
  const isoDate = text.match(/^(\d{4})-(\d{1,2})(?:-(\d{1,2}))?/)
  if (isoDate) return toYearMonth(Number(isoDate[1]), Number(isoDate[2]))
  return null
}

function getCalendarDays(yearMonth: string): number {
  const [year, month] = yearMonth.split('-').map(Number)
  return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

function getWeekdaysInMonth(yearMonth: string): number {
  const [year, month] = yearMonth.split('-').map(Number)
  const totalDays = getCalendarDays(yearMonth)
  let weekdays = 0
  for (let day = 1; day <= totalDays; day++) {
    const weekDay = new Date(Date.UTC(year, month - 1, day)).getUTCDay()
    if (weekDay !== 0 && weekDay !== 6) weekdays++
  }
  return weekdays
}

function makeJoinKey(yearMonth: string, store: string, gtin: string): string {
  return [yearMonth, store, gtin].join(KEY_SEPARATOR)
}

function makeStockKey(yearMonth: string, store: string, gtin: string, product: string): string {
  return [yearMonth, store, gtin, product].join(KEY_SEPARATOR)
}

function makeDisplayKey(yearMonth: string, store: string, gtin: string): string {
  return `${yearMonth.replace('-', '')}|${store}|${gtin}`
}

function roundLikeExcel(value: number, digits: number): number {
  if (!Number.isFinite(value)) return 0
  const factor = 10 ** digits
  return (Math.sign(value) * Math.round((Math.abs(value) + Number.EPSILON) * factor)) / factor
}

function compareText(a: string, b: string): number {
  if (a < b) return -1
  if (a > b) return 1
  return 0
}

function classifyRow(
  stock: number,
  flagNoSales: number,
  safetyStock: number,
  reorderPoint: number,
  currentCoverage: number,
  leadTime: number,
  targetCoverage: number
): CompreAgoraClassification {
  if (stock === 0) return 'Ruptura instalada'
  if (flagNoSales === 1) return 'Sem venda(avaliar)'
  if (safetyStock > stock) return 'Ruptura iminente'
  if (stock >= safetyStock && stock <= reorderPoint) return 'Perto do mínimo'
  if (roundLikeExcel(currentCoverage, 2) < roundLikeExcel(leadTime, 2)) return 'Cobertura crítica'
  if (roundLikeExcel(currentCoverage, 2) > roundLikeExcel(targetCoverage, 2)) return 'Excesso'
  return 'Saudável'
}

function getRowsAfterHeader(source: DetectedSource): SheetRow[] {
  return source.rows
    .slice(source.headerRowIndex + 1)
    .filter((row) => row.some((value) => getCellText(value) !== ''))
}

export function parseCompreAgoraBundle(
  files: CompreAgoraSourceFile[],
  options: CompreAgoraParserOptions
): CompreAgoraBundleResult {
  const {detected, unknownFiles, readErrors} = readDetectedSources(files)
  if (detected.length === 0 && readErrors.length === 0) {
    return {
      matched: false,
      errors: [],
      analyticRows: [],
      importRows: [],
      roleSummaries: [],
      unknownFiles,
    }
  }

  const errors = [...readErrors]
  const sourcesByRole = (Object.keys(REQUIRED_HEADERS) as CompreAgoraFileRole[]).reduce<
    Record<CompreAgoraFileRole, DetectedSource[]>
  >(
    (result, role) => {
      result[role] = detected.filter((source) => source.role === role)
      return result
    },
    {estoque: [], entradas: [], otif: []}
  )

  for (const role of Object.keys(sourcesByRole) as CompreAgoraFileRole[]) {
    if (sourcesByRole[role].length === 0)
      errors.push(`Arquivo obrigatório ausente: ${ROLE_LABELS[role]}.`)
  }
  if (unknownFiles.length > 0) {
    errors.push(
      `Arquivos não reconhecidos no pacote CompreAgora: ${unknownFiles.join(
        ', '
      )}. Selecione somente Estoque, Entradas e OTIF.`
    )
  }
  if (!Number.isFinite(options.leadTimeDays) || options.leadTimeDays <= 0) {
    errors.push('O parâmetro de lead time deve ser maior que zero.')
  }
  if (!Number.isFinite(options.targetCoverageDays) || options.targetCoverageDays <= 0) {
    errors.push('O parâmetro de cobertura alvo deve ser maior que zero.')
  }

  const roleSummaries: CompreAgoraRoleSummary[] = (
    Object.keys(sourcesByRole) as CompreAgoraFileRole[]
  ).map((role) => ({
    role,
    fileNames: sourcesByRole[role].map((source) => source.fileName),
    rowCount: sourcesByRole[role].reduce(
      (total, source) => total + getRowsAfterHeader(source).length,
      0
    ),
  }))

  if (errors.length > 0) {
    return {matched: true, errors, analyticRows: [], importRows: [], roleSummaries, unknownFiles}
  }

  const storeMap = options.storeCnpjByKey ?? DEFAULT_STORE_CNPJ_BY_KEY
  const stockAggregates = new Map<string, StockAggregate>()
  const entryAggregates = new Map<string, EntryAggregate>()
  const otifMonths = new Set<string>()

  for (const source of sourcesByRole.estoque) {
    const columns = buildColumnMap(source.rows[source.headerRowIndex])
    getRowsAfterHeader(source).forEach((row, index) => {
      const rowNumber = source.headerRowIndex + index + 2
      const anoMes = parseYearMonth(row[columns.data_referencia])
      const cnpjLoja = parseIdentifier(row[columns.cnpj_loja])
      const gtin = parseIdentifier(row[columns.ean])
      const produto = String(row[columns.descricao_produto] ?? '').trim()
      if (!anoMes || !cnpjLoja || !gtin || !produto) {
        errors.push(
          `${source.fileName}, linha ${rowNumber}: data de referência, loja, EAN ou produto inválido.`
        )
        return
      }

      const key = makeStockKey(anoMes, cnpjLoja, gtin, produto)
      const estoqueAtual = parseNumber(row[columns.estoque_atual]) ?? 0
      const current = stockAggregates.get(key)
      if (current) {
        current.vendaQtdMes += parseNumber(row[columns.saida]) ?? 0
        current.estoqueAtualQtd = Math.max(current.estoqueAtualQtd, estoqueAtual)
        current.saldoInicial = Math.max(
          current.saldoInicial,
          parseNumber(row[columns.saldo_inicial]) ?? 0
        )
        current.saldoFinal = Math.max(
          current.saldoFinal,
          parseNumber(row[columns.saldo_final]) ?? 0
        )
      } else {
        stockAggregates.set(key, {
          anoMes,
          cnpjLoja,
          gtin,
          produto,
          vendaQtdMes: parseNumber(row[columns.saida]) ?? 0,
          estoqueAtualQtd: estoqueAtual,
          saldoInicial: parseNumber(row[columns.saldo_inicial]) ?? 0,
          saldoFinal: parseNumber(row[columns.saldo_final]) ?? 0,
        })
      }
    })
  }

  for (const source of sourcesByRole.entradas) {
    const columns = buildColumnMap(source.rows[source.headerRowIndex])
    getRowsAfterHeader(source).forEach((row, index) => {
      const rowNumber = source.headerRowIndex + index + 2
      const anoMes = parseYearMonth(row[columns.data_recebimento])
      const storeKey = parseIdentifier(row[columns.loja_key])
      const cnpjLoja = storeMap[storeKey]
      const gtin = parseIdentifier(row[columns.gtin_principal])
      if (!cnpjLoja) {
        errors.push(`${source.fileName}, linha ${rowNumber}: loja_key "${storeKey}" sem de/para.`)
        return
      }
      if (!anoMes || !gtin) {
        errors.push(`${source.fileName}, linha ${rowNumber}: data de recebimento ou GTIN inválido.`)
        return
      }

      const key = makeJoinKey(anoMes, cnpjLoja, gtin)
      const current = entryAggregates.get(key) ?? {comprasQtd: 0, comprasRs: 0}
      current.comprasQtd += parseNumber(row[columns.quantidade_recebida]) ?? 0
      current.comprasRs += parseNumber(row[columns.ni_valor_bruto]) ?? 0
      entryAggregates.set(key, current)
    })
  }

  for (const source of sourcesByRole.otif) {
    const columns = buildColumnMap(source.rows[source.headerRowIndex])
    let validDates = 0
    getRowsAfterHeader(source).forEach((row) => {
      const anoMes = parseYearMonth(row[columns.data_pedido])
      if (!anoMes) return
      validDates++
      otifMonths.add(anoMes)
    })
    if (validDates === 0)
      errors.push(`${source.fileName}: nenhuma data de pedido válida encontrada no OTIF.`)
  }

  if (stockAggregates.size === 0)
    errors.push('Nenhuma linha válida encontrada no arquivo de Estoque.')
  if (entryAggregates.size === 0)
    errors.push('Nenhuma linha válida encontrada no arquivo de Entradas.')
  if (errors.length > 0) {
    return {matched: true, errors, analyticRows: [], importRows: [], roleSummaries, unknownFiles}
  }

  const getDaysConsidered = (yearMonth: string): number => {
    const configuredDays = options.daysConsideredByMonth?.[yearMonth]
    if (Number.isFinite(configuredDays) && (configuredDays as number) > 0)
      return configuredDays as number
    return otifMonths.has(yearMonth) ? getWeekdaysInMonth(yearMonth) : getCalendarDays(yearMonth)
  }

  const analyticWithStock = Array.from(stockAggregates.values()).map((stock) => {
    const entry = entryAggregates.get(makeJoinKey(stock.anoMes, stock.cnpjLoja, stock.gtin)) ?? {
      comprasQtd: 0,
      comprasRs: 0,
    }
    const vendaRs = entry.comprasRs
    const precoMedioRs = stock.vendaQtdMes === 0 ? 0 : vendaRs / stock.vendaQtdMes
    const diasDoMes = getDaysConsidered(stock.anoMes)
    const cmd = diasDoMes === 0 ? 0 : stock.vendaQtdMes / diasDoMes
    const coberturaAtualDias = cmd === 0 ? 0 : stock.estoqueAtualQtd / cmd
    const ss = cmd * options.leadTimeDays
    const ddlt = cmd * options.leadTimeDays
    const rop = ss + ddlt
    const estoqueAlvo = cmd * options.targetCoverageDays + ddlt + ss
    const pedidoSugerido = Math.max(0, estoqueAlvo - stock.estoqueAtualQtd)
    const flagSemVenda = stock.vendaQtdMes === 0 ? 1 : 0

    const analyticRow: CompreAgoraAnalyticRow = {
      anoMes: stock.anoMes,
      cnpjLoja: stock.cnpjLoja,
      gtin: stock.gtin,
      produto: stock.produto,
      vendaQtdMes: stock.vendaQtdMes,
      vendaRs,
      comprasQtd: entry.comprasQtd,
      comprasRs: entry.comprasRs,
      estoqueAtualQtd: stock.estoqueAtualQtd,
      precoMedioRs,
      diasDoMes,
      cmd,
      coberturaAtualDias,
      ltDias: options.leadTimeDays,
      flagLtDefault: 1,
      coberturaAlvoDias: options.targetCoverageDays,
      flagCoberturaDefault: 1,
      ss,
      ddlt,
      rop,
      estoqueAlvo,
      pedidoSugerido,
      flagSemVenda,
      qtdAjustadaUsuario: null,
      pedidoFinalQtd: pedidoSugerido,
      origemPedido: 'Modelo',
      flagAjustado: 0,
      classificacao: classifyRow(
        stock.estoqueAtualQtd,
        flagSemVenda,
        ss,
        rop,
        coberturaAtualDias,
        options.leadTimeDays,
        options.targetCoverageDays
      ),
      chave: makeDisplayKey(stock.anoMes, stock.cnpjLoja, stock.gtin),
    }
    return {analyticRow, stock}
  })

  analyticWithStock.sort(
    (a, b) =>
      compareText(a.analyticRow.anoMes, b.analyticRow.anoMes) ||
      compareText(a.analyticRow.cnpjLoja, b.analyticRow.cnpjLoja) ||
      compareText(a.analyticRow.gtin, b.analyticRow.gtin) ||
      compareText(a.analyticRow.produto, b.analyticRow.produto)
  )

  const latestImportRows = new Map<string, CompreAgoraImportRow>()
  for (const {analyticRow, stock} of analyticWithStock) {
    latestImportRows.set(makeJoinKey('', analyticRow.cnpjLoja, analyticRow.gtin), {
      ean: analyticRow.gtin,
      produto: analyticRow.produto,
      cnpjLoja: analyticRow.cnpjLoja,
      saldoInicial: stock.saldoInicial,
      entrada: analyticRow.comprasQtd,
      saida: analyticRow.vendaQtdMes,
      saldoFinal: stock.saldoFinal,
      estoqueAtual: analyticRow.estoqueAtualQtd,
    })
  }

  const importRows = Array.from(latestImportRows.values()).sort(
    (a, b) => compareText(a.cnpjLoja, b.cnpjLoja) || compareText(a.ean, b.ean)
  )

  return {
    matched: true,
    errors: [],
    analyticRows: analyticWithStock.map(({analyticRow}) => analyticRow),
    importRows,
    roleSummaries,
    unknownFiles,
  }
}
